import { existsSync, readFileSync } from 'fs';
import { join, resolve, extname } from 'path';
import { pathToFileURL } from 'url';
import { VibeshipConfig, PartialVibeshipConfig } from './types';
import { defaultConfig, getEnvironmentDefaults } from './defaults';
import { mergeConfigurations, applyEnvironmentOverrides, freezeConfig } from './merger';
import { validateConfig, validatePartialConfig } from './schema';

/**
 * Configuration file names to search for
 */
const CONFIG_FILE_NAMES = [
  'vibeship.config.ts',
  'vibeship.config.js',
  'vibeship.config.mjs',
  'vibeship.config.cjs',
  'vibeship.config.json',
  '.vibeshiprc.json',
  '.vibeshiprc',
];

/**
 * Configuration loader options
 */
export interface LoadConfigOptions {
  /**
   * Custom config path
   */
  configPath?: string;

  /**
   * Root directory to search for config
   */
  rootDir?: string;

  /**
   * Whether to apply environment overrides
   */
  applyEnvOverrides?: boolean;

  /**
   * Whether to validate the configuration
   */
  validate?: boolean;

  /**
   * Whether to freeze the configuration
   */
  freeze?: boolean;
}

/**
 * Load configuration from various sources
 */
export async function loadConfig(options: LoadConfigOptions = {}): Promise<VibeshipConfig> {
  const {
    configPath,
    rootDir = process.cwd(),
    applyEnvOverrides = true,
    validate = true,
    freeze = true,
  } = options;

  // 1. Find configuration file
  const configFile = configPath || findConfigFile(rootDir);
  
  // 2. Load user configuration
  let userConfig: PartialVibeshipConfig = {};
  if (configFile) {
    userConfig = await loadConfigFile(configFile);
    
    // Validate partial config if requested
    if (validate) {
      const validation = validatePartialConfig(userConfig);
      if (!validation.success) {
        throw new Error(
          `Invalid configuration in ${configFile}:\n${validation.errors?.join('\n')}`
        );
      }
    }
  }

  // 3. Get environment-specific defaults
  const environmentDefaults = getEnvironmentDefaults();

  // 4. Merge configurations
  let config = mergeConfigurations(
    defaultConfig,
    environmentDefaults,
    {}, // Environment overrides will be applied separately
    userConfig
  );

  // 5. Apply environment variable overrides
  if (applyEnvOverrides) {
    config = applyEnvironmentOverrides(config);
  }

  // 6. Validate final configuration
  if (validate) {
    const validation = validateConfig(config);
    if (!validation.success) {
      throw new Error(
        `Invalid final configuration:\n${validation.errors?.join('\n')}`
      );
    }
    config = validation.data!;
  }

  // 7. Freeze configuration to prevent mutations
  if (freeze) {
    config = freezeConfig(config);
  }

  return config;
}

/**
 * Find configuration file in the project
 */
function findConfigFile(rootDir: string): string | undefined {
  for (const fileName of CONFIG_FILE_NAMES) {
    const filePath = join(rootDir, fileName);
    if (existsSync(filePath)) {
      return filePath;
    }
  }

  // Check parent directories up to 3 levels
  let currentDir = rootDir;
  for (let i = 0; i < 3; i++) {
    const parentDir = resolve(currentDir, '..');
    if (parentDir === currentDir) break; // Reached root

    for (const fileName of CONFIG_FILE_NAMES) {
      const filePath = join(parentDir, fileName);
      if (existsSync(filePath)) {
        return filePath;
      }
    }
    currentDir = parentDir;
  }

  return undefined;
}

/**
 * Load configuration from a file
 */
async function loadConfigFile(filePath: string): Promise<PartialVibeshipConfig> {
  const ext = extname(filePath);

  switch (ext) {
    case '.ts':
    case '.mjs':
      return loadTypeScriptConfig(filePath);
    
    case '.js':
    case '.cjs':
      return loadJavaScriptConfig(filePath);
    
    case '.json':
    case '':
      return loadJsonConfig(filePath);
    
    default:
      throw new Error(`Unsupported configuration file format: ${ext}`);
  }
}

/**
 * Load TypeScript configuration file
 */
async function loadTypeScriptConfig(filePath: string): Promise<PartialVibeshipConfig> {
  // Check if we can use native TypeScript loading
  const supportsTS = await checkTypeScriptSupport();
  
  if (supportsTS) {
    // Use dynamic import with TypeScript support
    const fileUrl = pathToFileURL(filePath).href;
    const module = await import(fileUrl);
    return extractConfigFromModule(module);
  }

  // Fallback: Try to use ts-node or tsx if available
  try {
    // Try tsx first (faster)
    await tryRegisterLoader('tsx/cjs');
  } catch {
    try {
      // Fallback to ts-node
      await tryRegisterLoader('ts-node/register');
    } catch {
      throw new Error(
        'TypeScript configuration files require either:\n' +
        '1. Running with --loader tsx or --loader ts-node/esm\n' +
        '2. Having tsx or ts-node installed\n' +
        '3. Using a .js configuration file instead'
      );
    }
  }

  // Now try loading again
  const fileUrl = pathToFileURL(filePath).href;
  const module = await import(fileUrl);
  return extractConfigFromModule(module);
}

/**
 * Load JavaScript configuration file
 */
async function loadJavaScriptConfig(filePath: string): Promise<PartialVibeshipConfig> {
  const fileUrl = pathToFileURL(filePath).href;
  const module = await import(fileUrl);
  return extractConfigFromModule(module);
}

/**
 * Load JSON configuration file
 */
function loadJsonConfig(filePath: string): PartialVibeshipConfig {
  const content = readFileSync(filePath, 'utf-8');
  try {
    return JSON.parse(content);
  } catch (error) {
    throw new Error(`Invalid JSON in configuration file ${filePath}: ${error}`);
  }
}

/**
 * Extract configuration from module
 */
function extractConfigFromModule(module: any): PartialVibeshipConfig {
  // Support various export formats
  if (module.default) {
    // ES modules default export
    if (typeof module.default === 'function') {
      return module.default();
    }
    return module.default;
  }

  if (module.config) {
    // Named export
    if (typeof module.config === 'function') {
      return module.config();
    }
    return module.config;
  }

  if (typeof module === 'function') {
    // Direct function export
    return module();
  }

  if (typeof module === 'object' && module !== null) {
    // Direct object export
    return module;
  }

  throw new Error('Configuration file must export a configuration object');
}

/**
 * Check if TypeScript is natively supported
 */
async function checkTypeScriptSupport(): Promise<boolean> {
  // Check if running with tsx or ts-node loader
  if (process.env.TSX || process.env.TS_NODE_DEV) {
    return true;
  }

  // Check Node.js version for native TypeScript support
  const nodeVersion = process.version;
  const major = parseInt(nodeVersion.split('.')[0].substring(1));
  
  // Node.js 20+ with --experimental-strip-types
  if (major >= 20 && process.execArgv.includes('--experimental-strip-types')) {
    return true;
  }

  return false;
}

/**
 * Try to register a TypeScript loader
 */
async function tryRegisterLoader(loader: string): Promise<void> {
  try {
    await import(loader);
  } catch (error) {
    throw new Error(`Failed to load ${loader}`);
  }
}

/**
 * Create a configuration helper function for use in config files
 */
export function defineConfig<T extends PartialVibeshipConfig = PartialVibeshipConfig>(config: T): T {
  return config;
}

/**
 * Watch configuration file for changes
 */
export async function watchConfig(
  options: LoadConfigOptions & { onChange: (config: VibeshipConfig) => void }
): Promise<() => void> {
  const { onChange, ...loadOptions } = options;
  const configFile = options.configPath || findConfigFile(options.rootDir || process.cwd());

  if (!configFile) {
    // No config file, just return current config
    const config = await loadConfig(loadOptions);
    onChange(config);
    return () => {}; // No-op cleanup
  }

  // Initial load
  let currentConfig = await loadConfig(loadOptions);
  onChange(currentConfig);

  // Watch for changes
  const { watch } = await import('fs');
  const watcher = watch(configFile, async (eventType) => {
    if (eventType === 'change') {
      try {
        // Clear module cache for the config file
        delete require.cache[require.resolve(configFile)];
        
        // Reload configuration
        const newConfig = await loadConfig(loadOptions);
        
        // Only notify if config actually changed
        if (JSON.stringify(newConfig) !== JSON.stringify(currentConfig)) {
          currentConfig = newConfig;
          onChange(newConfig);
        }
      } catch (error) {
        console.error('Error reloading configuration:', error);
      }
    }
  });

  // Return cleanup function
  return () => {
    watcher.close();
  };
}

/**
 * Get configuration with caching
 */
let cachedConfig: VibeshipConfig | null = null;
let cacheKey: string | null = null;

export async function getConfig(options: LoadConfigOptions = {}): Promise<VibeshipConfig> {
  const key = JSON.stringify(options);
  
  if (cachedConfig && cacheKey === key) {
    return cachedConfig;
  }

  cachedConfig = await loadConfig(options);
  cacheKey = key;
  
  return cachedConfig;
}

/**
 * Clear configuration cache
 */
export function clearConfigCache(): void {
  cachedConfig = null;
  cacheKey = null;
}